/**
 * AdsTab — read-only view of the Meta ad account. Shows every campaign
 * synced from Meta Ads Manager with spend / impressions / reach / CTR /
 * CPM, and drilldown into individual ads (with their IG post thumbnail
 * when the ad boosts an organic IG post).
 *
 * All boost creation / pause / resume happens in Meta Ads Manager
 * directly — Studio is read-only for ads.
 */
import { useState } from 'react';
import { CheckCircle2, ExternalLink, Loader2, PauseCircle, RefreshCw, XCircle } from 'lucide-react';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/charts/EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useCampaigns, useAdsForCampaign, refreshAdsSync, type Campaign, type Ad } from '@/hooks/useAds';

export function AdsTab() {
  const campaigns = useCampaigns();
  const [openId, setOpenId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);

  const all = campaigns.data ?? [];
  const rows = activeOnly ? all.filter((c) => c.effective_status === 'ACTIVE') : all;

  async function refresh() {
    setRefreshing(true);
    setRefreshMsg(null);
    const r = await refreshAdsSync();
    setRefreshing(false);
    if (!r.ok) setRefreshMsg(r.error ?? 'refresh failed');
    else {
      setRefreshMsg(`Synced ${r.campaigns_synced} campaigns · ${r.ads_synced} ads`);
      campaigns.refetch();
    }
  }

  if (campaigns.isLoading) return <Skeleton className="h-40 w-full" />;

  const totalSpend = rows.reduce((s, r) => s + (Number(r.spend_inr) || 0), 0);
  const totalReach = rows.reduce((s, r) => s + (r.reach || 0), 0);
  const totalImpressions = rows.reduce((s, r) => s + (r.impressions || 0), 0);
  const activeCount = all.filter((r) => r.effective_status === 'ACTIVE').length;

  return (
    <div className="space-y-4">
      <SectionHeader
        title={<span>Ads · <code className="text-xs font-normal text-muted-foreground">act_517642365920307</code></span>}
        description="Read-only mirror of the FLAX Meta ad account. Manage / pause / boost in Meta Ads Manager."
        actions={
          <div className="flex items-center gap-2">
            <label className="flex cursor-pointer items-center gap-1.5 rounded-md border border-border/50 px-2.5 py-1.5 text-xs">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
                className="h-3.5 w-3.5 cursor-pointer accent-violet-500"
              />
              Active only
            </label>
            {refreshMsg && (
              <span className={cn('text-2xs', refreshMsg.startsWith('Synced') ? 'text-emerald-500' : 'text-rose-500')}>{refreshMsg}</span>
            )}
            <Button size="sm" variant="outline" onClick={refresh} disabled={refreshing}>
              {refreshing ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Syncing…</> : <><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh</>}
            </Button>
            <a
              href="https://business.facebook.com/adsmanager/manage/campaigns"
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-violet-500 hover:underline"
            >
              Open Ads Manager <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        }
      />

      {rows.length === 0 ? (
        <EmptyState
          title={activeOnly ? 'No active campaigns' : 'No campaigns synced yet'}
          description={activeOnly ? `You have ${all.length} campaigns total — uncheck 'Active only' to see them all.` : "Hit Refresh above once you've created a campaign in Meta Ads Manager."}
        />
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-4">
            <SummaryCard label={activeOnly ? 'Showing' : 'Active'} value={activeOnly ? `${rows.length}` : `${activeCount} / ${all.length}`} tone="emerald" />
            <SummaryCard label="Total spend" value={`₹${totalSpend.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} tone="violet" />
            <SummaryCard label="Total reach" value={compactN(totalReach)} tone="amber" />
            <SummaryCard label="Total impressions" value={compactN(totalImpressions)} tone="rose" />
          </div>

          <div className="space-y-2">
            {rows.map((c) => (
              <CampaignCard
                key={c.id}
                camp={c}
                open={openId === c.id}
                onToggle={() => setOpenId(openId === c.id ? null : c.id)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, tone }: { label: string; value: string; tone: 'emerald' | 'amber' | 'violet' | 'rose' }) {
  const toneMap = { emerald: 'text-emerald-500', amber: 'text-amber-500', violet: 'text-violet-500', rose: 'text-rose-500' };
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-2xs uppercase text-muted-foreground">{label}</div>
        <div className={cn('mt-1 text-2xl font-semibold tabular-nums', toneMap[tone])}>{value}</div>
      </CardContent>
    </Card>
  );
}

function CampaignCard({ camp, open, onToggle }: { camp: Campaign; open: boolean; onToggle: () => void }) {
  const ads = useAdsForCampaign(open ? camp.id : null);

  return (
    <Card>
      <CardContent className="p-3">
        <button type="button" onClick={onToggle} className="block w-full text-left">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <StatusPill status={camp.effective_status} />
            {camp.objective && (
              <span className="rounded bg-muted/40 px-1.5 py-0.5 text-2xs text-muted-foreground">
                {camp.objective.replace('OUTCOME_', '').toLowerCase()}
              </span>
            )}
            <span className="min-w-0 flex-1 truncate font-medium">{camp.name ?? camp.id}</span>
            <span className="text-2xs text-muted-foreground">
              {camp.updated_time ? new Date(camp.updated_time).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''}
            </span>
          </div>

          <div className="mt-2 grid grid-cols-2 gap-2 text-xs sm:grid-cols-6">
            <Metric label="Budget/day" value={camp.daily_budget_inr != null ? `₹${camp.daily_budget_inr}` : '—'} />
            <Metric label="Spend" value={camp.spend_inr != null ? `₹${Number(camp.spend_inr).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—'} tone="emerald" />
            <Metric label="Impr." value={compactN(camp.impressions ?? 0)} />
            <Metric label="Reach" value={compactN(camp.reach ?? 0)} />
            <Metric label="Clicks" value={compactN(camp.clicks ?? 0)} />
            <Metric label="CTR" value={camp.ctr != null ? `${(Number(camp.ctr) * 100).toFixed(2)}%` : '—'} />
          </div>
        </button>

        {open && (
          <div className="mt-3 border-t border-border/40 pt-2">
            {ads.isLoading ? (
              <div className="py-3 text-2xs text-muted-foreground">loading ads…</div>
            ) : (ads.data ?? []).length === 0 ? (
              <div className="py-3 text-2xs text-muted-foreground">no ads under this campaign</div>
            ) : (
              <div className="space-y-1.5">
                {ads.data!.map((a) => <AdRow key={a.id} ad={a} />)}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AdRow({ ad }: { ad: Ad }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border/40 bg-muted/10 p-2">
      {ad.ig_thumbnail_url ? (
        <img src={ad.ig_thumbnail_url} alt="" referrerPolicy="no-referrer" className="h-10 w-10 shrink-0 rounded object-cover" />
      ) : (
        <div className="h-10 w-10 shrink-0 rounded bg-muted/40" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <StatusPill status={ad.effective_status} small />
          <span className="min-w-0 flex-1 truncate text-xs">{ad.name ?? ad.id}</span>
        </div>
        <div className="mt-0.5 flex gap-3 text-2xs tabular-nums text-muted-foreground">
          <span>₹{Number(ad.spend_inr ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
          <span>{compactN(ad.impressions ?? 0)} impr</span>
          <span>{compactN(ad.reach ?? 0)} reach</span>
          <span>{compactN(ad.clicks ?? 0)} clicks</span>
          {ad.ctr != null && <span>{(Number(ad.ctr) * 100).toFixed(2)}% CTR</span>}
        </div>
      </div>
      {ad.ig_permalink && (
        <a href={ad.ig_permalink} target="_blank" rel="noreferrer" className="text-2xs text-violet-500 hover:underline">
          IG post →
        </a>
      )}
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: 'emerald' }) {
  return (
    <div>
      <div className="text-2xs uppercase text-muted-foreground">{label}</div>
      <div className={cn('font-semibold tabular-nums', tone === 'emerald' && 'text-emerald-500')}>{value}</div>
    </div>
  );
}

function StatusPill({ status, small }: { status: string | null; small?: boolean }) {
  const cls = small ? 'text-2xs px-1.5 py-0.5' : 'text-2xs px-2 py-0.5';
  const c = statusStyle(status);
  return (
    <span className={cn('flex items-center gap-1 rounded-full font-medium', cls, c.className)}>
      {c.icon} {status ?? '—'}
    </span>
  );
}

function statusStyle(status: string | null): { icon: React.ReactNode; className: string } {
  const s = (status ?? '').toUpperCase();
  if (s === 'ACTIVE') return { icon: <CheckCircle2 className="h-3 w-3" />, className: 'bg-emerald-500/10 text-emerald-500' };
  if (s === 'PAUSED') return { icon: <PauseCircle className="h-3 w-3" />, className: 'bg-amber-500/10 text-amber-500' };
  if (s.includes('DISAPPROVED') || s === 'DELETED' || s === 'ARCHIVED') return { icon: <XCircle className="h-3 w-3" />, className: 'bg-rose-500/10 text-rose-500' };
  return { icon: null, className: 'bg-muted/40 text-muted-foreground' };
}

function compactN(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}
